import { ReparoApi } from './index';

// Types
export interface MaterialBalanceDto {
    materialId: string;
    materialName: string;
    category: string;
    unit?: string;
    balance: number;
    totalInward: number;
    totalConsumed: number;
    lastInwardDate: string | null;
    lastConsumedDate: string | null;
}

export interface LedgerEventDto {
    id: string;
    merchantId: string;
    vendorId: string;
    materialId: string;
    unit?: string;
    eventType: 'INWARD' | 'CONSUMPTION' | 'ADJUSTMENT' | 'OPENING_BALANCE';
    quantity: number;
    date: string;
    purchaseOrderId: string | null;
    referenceId: string | null;
    notes: string | null;
    createdBy: string | null;
    createdAt: string;
    updatedAt: string;
    /** Names come from the join; blank when the row could not be matched. */
    vendorName?: string;
    materialName?: string;
    /** Per-unit price paid. Null until the brand records it. Brand-visible only. */
    unitCost?: number | null;
    currency?: string | null;
}

export interface HistoryResponse {
    events: LedgerEventDto[];
    total: number;
    page: number;
    hasNext: boolean;
}

export interface UploadMappingItem {
    uploadName: string;
    materialId: string;
}

export interface SuggestedMapping {
    uploadName: string;
    suggestedMaterialId: string | null;
    suggestedMaterialName: string | null;
    confidence: number;
}

export interface UploadPreviewResponse {
    rows: Record<string, string>[];
    suggestedMappings: SuggestedMapping[];
    unmatchedNames: string[];
}

// Vendor portal APIs
export const uploadPreview = async (body: { format: string; data: string }): Promise<UploadPreviewResponse> =>
    ReparoApi({ method: 'POST', url: '/vendor-portal/upload/preview', body: JSON.stringify(body) });

export const uploadCommit = async (body: {
    format: string;
    data: string;
    mappings: UploadMappingItem[];
}): Promise<{ processed: number; errors: string[] }> =>
    ReparoApi({ method: 'POST', url: '/vendor-portal/upload', body: JSON.stringify(body) });

// Customer APIs
export const getCrossVendorBalances = async (body: {
    vendorIds?: string[];
    materialIds?: string[];
}): Promise<{ balances: MaterialBalanceDto[] }> =>
    ReparoApi({ method: 'POST', url: '/vendor-stock/balances', body: JSON.stringify(body) });

export const getLedgerHistory = async (body: {
    vendorId?: string;
    materialId?: string;
    dateFrom?: string;
    dateTo?: string;
    eventType?: string;
    purchaseOrderId?: string;
    /** Only receipts still awaiting a cost. Implies inward movements. */
    uncostedOnly?: boolean;
    page?: number;
    pageSize?: number;
}): Promise<HistoryResponse> =>
    ReparoApi({ method: 'POST', url: '/vendor-stock/history', body: JSON.stringify(body) });

// ─── Material cost (brand only) ─────────────────────────────────
// The manufacturer never sees these: what a brand pays its suppliers is its own
// commercial data, and the CM portal is an external login. Both routes are ADMIN-only.

/**
 * Record what was paid for a receipt the manufacturer already posted.
 * Setting a cost that is unset completes the record; changing one is refused.
 */
export const setReceiptCost = async (body: {
    eventId: string;
    unitCost: number;
    currency?: string;
}): Promise<{ eventId: string; unitCost: number | null; currency: string | null }> =>
    ReparoApi({
        method: 'POST',
        url: '/vendor-stock/receipt-cost',
        body: JSON.stringify(body),
    });

export interface AveragePricePaidRow {
    materialId: string;
    currency: string;
    averageUnitCost: number;
    /** Quantity the average actually covers, against everything received. */
    costedQuantity: number;
    totalQuantity: number;
    receipts: number;
}

/**
 * Average price paid per material, grouped by currency and never converted.
 * Not a stock valuation - consumption does not enter it.
 */
export const getAveragePricePaid = async (body: {
    materialIds?: string[];
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
}): Promise<{ rows: AveragePricePaidRow[] }> =>
    ReparoApi({
        method: 'POST',
        url: '/vendor-stock/average-price-paid',
        body: JSON.stringify(body),
    });

// ─── Bucketed stock ────────────────────────────────────────────

export type BatchAvailability = 'USABLE' | 'IN_QC' | 'BLOCKED';

export interface BucketedBatchLine {
    /** The empty string is the no-batch bucket, not a missing value. */
    batchId: string;
    batchCode: string | null;
    manufacturingDate: string | null;
    expiryDate: string | null;
    qcStatus: string | null;
    /** When the batch was first recorded — how long it has been sitting. */
    receivedAt: string | null;
    availability: BatchAvailability;
    quantity: number;
}

export interface BucketedItemBalance {
    warehouseId: string;
    itemId: string;
    /** What a person calls it. Resolved by the server; the id stays for actions. */
    itemName?: string | null;
    itemType: string;
    usable: number;
    inQc: number;
    /** Failed, expired, or kept bad at the gate. */
    blocked: number;
    /** The part of `blocked` refused at the gate and kept on the floor. No batch. */
    bad?: number;
    total: number;
    /**
     * How much of this the site has ever made — received, for material.
     *
     * The batch register's per-run figure rolled up to the item. A FLOW sitting beside four
     * balances, so it can exceed all of them: a run that has shipped is still a run that
     * happened, and the column would be worthless if dispatching hid it.
     */
    manufactured?: number;
    batches: BucketedBatchLine[];
}

/**
 * What this site holds, split three ways, with the batches behind each figure. The site comes
 * from the login rather than the request.
 */
export const getBucketedStock = async (body: {
    itemType?: 'material' | 'product';
    itemIds?: string[];
}): Promise<{ items: BucketedItemBalance[] }> =>
    ReparoApi({
        method: 'POST',
        url: '/vendor-portal/stock/buckets',
        body: JSON.stringify(body),
    });

// ─── QC verdicts and disposition ───────────────────────────────

export type BatchQcStatus = 'PENDING' | 'PASSED' | 'FAILED';

export type BatchDisposition = 'RETURN_TO_SUPPLIER' | 'DISPOSE';

/**
 * Record a QC verdict.
 *
 * Every transition is allowed, including releasing a batch that previously failed — QC gets
 * things wrong, and a batch is identified by its printed code, so there is no "receive it again
 * properly" that does not land on this same batch. Releasing a failed batch needs a reason, and
 * comes back flagged `notable` so the caller knows the brand will see it.
 */
export const markBatchQc = async (
    batchId: string,
    body: { qcStatus: BatchQcStatus; notes?: string },
): Promise<{ notable: boolean }> =>
    ReparoApi({
        method: 'POST',
        url: `/vendor-portal/batches/${batchId}/qc`,
        body: JSON.stringify(body),
    });

/** Record stock leaving a batch by being sent back or destroyed, rather than used or shipped. */
export const disposeBatch = async (
    batchId: string,
    body: {
        disposition: BatchDisposition;
        quantity: number;
        reasonCode?: string;
        notes?: string;
    },
): Promise<{ eventIds: string[] }> =>
    ReparoApi({
        method: 'POST',
        url: `/vendor-portal/batches/${batchId}/dispose`,
        body: JSON.stringify(body),
    });

// ─── Reference data and receiving ──────────────────────────────

export interface CatalogMaterial {
    itemId: string;
    name: string;
    unit: string;
    /** 'RM' or 'PM' — decides which batch-tracking rule applies to it. */
    materialType: string;
    /**
     * The vendors the brand has mapped as suppliers of this material — the only ones the
     * supplier picker offers for it, and the only ones the server accepts on a receipt. Empty
     * means nobody is mapped yet; the brand fixes that in its vendor mappings, and the list is
     * never widened here.
     */
    supplierIds: string[];
}

export interface VendorCatalog {
    /**
     * Only the raw and packaging material on the BOM of what this site makes — the server
     * refuses anything else on receipt, so this list is the whole truth of what may be entered.
     * Empty when nothing the site makes has a BOM yet; the fix is the brand's, not a wider list.
     */
    materials: CatalogMaterial[];
    /** Only the active finished goods this site is mapped to or has an order for. */
    products: Array<{ itemId: string; name: string; unitsPerCase?: number | null }>;
    suppliers: Array<{ vendorId: string; name: string }>;
    /**
     * True on the site's own door: the brand's sourcing is not the site's to see, so no
     * supplier is offered, asked for, or required. The server attributes each receipt.
     */
    suppliersHidden?: boolean;
    /** Keyed 'RM' | 'PM' | 'FG'; 'full' means a batch code is required for that kind. */
    batchTracking: Record<string, string>;
    /** 'auto' | 'manual' | 'assisted' — whether the split is pre-filled, and whether editable. */
    rmConsumption: string;
    /** 'open' | 'against_po' — whether a delivery starts from an open purchase order. */
    receivingMode?: string;
}

/** Items, suppliers and batch rules — everything the entry grids need to offer a choice. */
export const getVendorCatalog = async (): Promise<VendorCatalog> =>
    ReparoApi({ method: 'GET', url: '/vendor-portal/catalog' });

export interface SiteBomRow {
    productId: string;
    productSku: string | null;
    productName: string;
    materialId: string;
    materialSku: string | null;
    materialName: string;
    materialType: 'RM' | 'PM';
    unit: string;
    quantity: number;
    /** The recipe this line belongs to: this site's vendor, or null for the brand's default. */
    vendorId: string | null;
    vendorName: string | null;
}

/**
 * The recipes this site works to, for the products it makes.
 *
 * Scoped on the server to this site's own products — a manufacturer never sees another's
 * formulation — and resolved to the one recipe a run here would actually consume.
 */
export const getSiteBom = async (): Promise<{
    vendorId: string | null;
    rows: SiteBomRow[];
}> => ReparoApi({ method: 'GET', url: '/vendor-portal/bom' });

/** What the site found when it unloaded a line. Mirrors the server's ReceiptQcVerdict. */
export type ReceiptQcVerdict = 'ALL_APPROVED' | 'SOME_REJECTED' | 'ALL_REJECTED';

/** Why material was refused at the gate. Mirrors the server's MaterialRejectionReason. */
export type MaterialRejectionReason =
    | 'DAMAGED_IN_TRANSIT'
    | 'WRONG_PRINT_OR_ITEM'
    | 'SHORT_DATED'
    | 'QUANTITY_SHORT'
    | 'OTHER_SUPPLIER'
    | 'DAMAGED_AT_SITE'
    | 'OTHER_SITE';

/** Where refused stock goes: back to the supplier, in the bin, or kept at the site as bad. */
export type RejectionAction = 'RETURN' | 'SCRAP' | 'KEEP';

export interface ReceiveLine {
    itemId: string;
    quantity: number;
    batchCode?: string;
    supplierId?: string;
    manufacturingDate?: string;
    expiryDate?: string;
    /** The site's verdict at the gate. Required: a line with none is not a receipt. */
    qcVerdict: ReceiptQcVerdict;
    /** Only for SOME_REJECTED, and then strictly between 0 and `quantity`. */
    rejectedQuantity?: number;
    /** Required whenever anything is refused. */
    rejectionReason?: MaterialRejectionReason;
    /** Required whenever anything is refused; Return is refused with a site reason. */
    rejectionAction?: RejectionAction;
    purchaseOrderId?: string;
    /**
     * What to do when this item brings the order above what it asked for. Honoured only on the
     * brand's door and only against a purchase order; absent, the server refuses as it always has.
     */
    overDeliveryDecision?: OverDeliveryDecision;
    /** The stock transfer the goods came on, instead of a purchase order. */
    stnId?: string;
    notes?: string;
    /**
     * Per unit, excluding GST. Honoured only when the brand is recording on the site's
     * behalf; the server ignores it on the manufacturer's own path.
     */
    unitCost?: number;
    currency?: string;
}

/**
 * Record a delivery. The whole note in one call — it lands whole or not at all, so a rejection
 * means fixing the row it names rather than working out what already went in.
 */
export const postReceive = async (body: {
    reference?: string;
    receivedDate?: string;
    /** Who keyed it in — the shared site login cannot name them. */
    enteredByName?: string;
    lines: ReceiveLine[];
}): Promise<ReceiveResult> =>
    ReparoApi({
        method: 'POST',
        url: '/vendor-portal/receive',
        body: JSON.stringify(body),
    });

/** What saving a delivery hands back: its rows, and the goods receipt number it was filed under. */
/** What the brand chose to do with goods above the order: keep them at the order's rate, or free. */
export type OverDeliveryDecision = 'PAY' | 'FREE';

export interface ReceiveResult {
    eventIds: string[];
    grnNumber: string;
    /** What the delivery said, as the ledger understood it. */
    receipt?: {
        grnNumber: string;
        purchaseOrderId: string | null;
        /** Goods taken in above the order, per item, and on what terms the brand kept them. */
        overDeliveries: Array<{ itemName: string; unit: string; quantity: number; decision: OverDeliveryDecision }>;
    };
}

/**
 * An open purchase order, or a stock transfer on its way to the site, that a delivery can be
 * received against, with what it still owes per item. Per item, not per line - an item split
 * across two lines owes their sum.
 */
export interface ReceivableOrder {
    /** The PO number, or the transfer's code. */
    orderNumber: string;
    /** Null on the site's own door: the brand's sourcing is not the site's to see. */
    vendorId: string | null;
    vendorName: string | null;
    /** STN for a stock transfer from the brand's own warehouse; absent on a purchase order. */
    kind?: 'STN';
    /** The transfer each receipt line names. STN only. */
    stnId?: string;
    fromNodeName?: string | null;
    dispatchedAt?: string | null;
    lines: Array<{
        itemId: string;
        description: string | null;
        ordered: number;
        received: number;
        remaining: number;
        /** The order's rate for the item. Brand's door only; null on the site's own. */
        unitPrice?: number | null;
    }>;
}

/** The open orders this site can receive against. The site comes from the login. */
export const getReceivableOrders = async (): Promise<ReceivableOrder[]> =>
    ReparoApi({ method: 'GET', url: '/vendor-portal/receivable-orders' });

// ─── Declaring production ──────────────────────────────────────

export interface DeclarationRequest {
    /**
     * Omitted when the brand does not track finished-goods batches (`batchTracking.FG` is
     * 'off'): the run lands in the no-batch bucket against its work order. A brand that
     * tracks them is refused a declaration without one, so the form never hides it for those.
     */
    batch?: {
        code: string;
        manufacturingDate?: string;
        expiryDate?: string;
        /**
         * The verdict, when the run was checked as it came off. Omitted means not checked yet,
         * and the goods are held in QC until somebody releases them.
         */
        qcStatus?: 'PASSED' | 'FAILED';
    };
    outputs: Array<{ productId: string; quantity: number }>;
    consumption?: Array<{
        materialId: string;
        quantity: number;
        batches?: Array<{ batchId: string; quantity: number }>;
    }>;
    wastage?: Array<{
        materialId: string;
        quantity: number;
        reasonCode?: string;
        batchId?: string;
    }>;
    /** Why a batch other than the suggested one was used — one reason for the run. */
    deviationReason?: string;
    workOrderId?: string;
    date?: string;
    reference?: string;
    enteredByName?: string;
    notes?: string;
}

/**
 * A declaration that names its batch. The batch grid builds one of these per code - a grid
 * row without a code is not a declaration at all - so its readers may take the batch as given
 * without re-checking what the grid already guaranteed.
 */
export type BatchedDeclarationRequest = DeclarationRequest & {
    batch: NonNullable<DeclarationRequest['batch']>;
};

export interface AllocationLine {
    batchId: string;
    batchCode: string | null;
    quantity: number;
}

export interface PreviewedConsumption {
    materialId: string;
    /** What a person calls it. Resolved by the server. */
    materialName?: string | null;
    required: number;
    shortfall: number;
    deviatesFromFefo: boolean;
    lines: AllocationLine[];
}

export type DeclarationExit =
    | 'RECORD_INWARD'
    | 'RELEASE_QC'
    | 'ENTER_ACTUAL_CONSUMPTION'
    | 'REDUCE_QUANTITY';

export interface DeclarationBlocked {
    status: 'BLOCKED';
    shortfalls: Array<{
        materialId: string;
        /** What a person calls it. Resolved by the server. */
        materialName?: string | null;
        required: number;
        available: number;
        shortfall: number;
    }>;
    exits: DeclarationExit[];
    maxDeclarable: Array<{ productId: string; quantity: number }> | null;
    releasableByQc: Record<string, number>;
}

export interface DeclarationRecorded {
    status: 'RECORDED';
    productionEntryId: string;
    eventIds: string[];
    /** `batchCode` is null when the brand does not track finished-goods batches. */
    outputs: Array<{ productId: string; quantity: number; batchCode: string | null }>;
    consumption: Array<{
        materialId: string;
        /** What a person calls it. Resolved by the server. */
        materialName?: string | null;
        batchId: string;
        batchCode: string | null;
        quantity: number;
        deviatedFromFefo: boolean;
    }>;
    bomMissing: boolean;
}

export interface DeclarationPreview {
    consumption: PreviewedConsumption[];
    /** The block that WOULD come back on submit, or null when the run can be recorded. */
    blocked: DeclarationBlocked | null;
    bomMissing: boolean;
}

/** What the run would consume and whether it could be recorded — writes nothing. */
export const previewDeclaration = async (
    body: DeclarationRequest,
): Promise<DeclarationPreview> =>
    ReparoApi({
        method: 'POST',
        url: '/vendor-portal/declare/preview',
        body: JSON.stringify(body),
    });

/**
 * Record the run. Comes back RECORDED or BLOCKED — a block is a 200 with a table of what is
 * short and named ways out, not an error.
 */
export const declareProduction = async (
    body: DeclarationRequest,
): Promise<DeclarationRecorded | DeclarationBlocked> =>
    ReparoApi({
        method: 'POST',
        url: '/vendor-portal/declare',
        body: JSON.stringify(body),
    });

// ─── The site ledger ───────────────────────────────────────────

export type MovementType =
    | 'RM_INWARD'
    | 'RM_CONSUMPTION'
    | 'RM_RETURN'
    | 'FG_PRODUCTION'
    | 'FG_DISPATCH'
    | 'WASTAGE'
    | 'ADJUSTMENT'
    | 'OPENING_BALANCE'
    | 'MARKED_BAD';

export type EntrySource = 'CM_PORTAL' | 'BRAND_ON_BEHALF' | 'OPENING_CSV';

export interface SiteLedgerEntry {
    id: string;
    /** When the movement happened. */
    date: string;
    /** When it was recorded, which is what the undo window runs from. */
    recordedAt: string;
    /** Null on sync-ingested rows, which predate the concept. */
    movementType: MovementType | null;
    incoming: boolean;
    quantity: number;
    itemId: string;
    /** What a person calls it. Resolved by the server; the id stays for actions. */
    itemName?: string | null;
    itemType: string;
    /** Per-unit price paid. Null until the brand records it. Brand-visible only. */
    unitCost?: number | null;
    currency?: string | null;
    batchId: string | null;
    batchCode: string | null;
    batchExpiryDate: string | null;
    /** 'SITE_RECEIPT' while the brand still has to say who sent this batch. */
    batchOriginType?: string | null;
    reference: string | null;
    reasonCode: string | null;
    notes: string | null;
    /** Null means the row came from a connected system rather than a person. */
    entrySource: EntrySource | null;
    enteredByName: string | null;
    createdBy: string | null;
    /** Rows sharing this belong to one production declaration. */
    productionEntryId: string | null;
    /** The movement this one undoes. */
    reversalOfId: string | null;
    /** The movement that undoes this one, when there is one. */
    reversedByEventId: string | null;
    workOrderId: string | null;
    purchaseOrderId: string | null;
    /** The goods receipt a delivery's rows - and their undo - share. Null on everything else. */
    grnId?: string | null;
}

export interface SiteLedgerResponse {
    entries: SiteLedgerEntry[];
    total: number;
    page: number;
    pageSize: number;
}

/** Movement history for the site this login belongs to. The site comes from the login. */
export const getSiteLedger = async (body: {
    movementTypes?: MovementType[];
    itemIds?: string[];
    itemType?: 'material' | 'product';
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
}): Promise<SiteLedgerResponse> =>
    ReparoApi({ method: 'POST', url: '/vendor-portal/ledger', body: JSON.stringify(body) });

/**
 * Undo a movement by recording its opposite.
 *
 * Reverses the whole declaration when the event belongs to one — finished goods cannot stay on
 * the shelf while the material that made them comes back.
 */
export const reverseMovement = async (
    eventId: string,
    body: { notes?: string } = {},
): Promise<{ eventIds: string[]; reversedCount: number }> =>
    ReparoApi({
        method: 'POST',
        url: `/vendor-portal/ledger/${eventId}/reverse`,
        body: JSON.stringify(body),
    });

// ─── The order queue ───────────────────────────────────────────

/** A run on the line: what it was set out to make, and what has come off it so far. */
export interface SiteOpenRun {
    batchId: string;
    code: string;
    planned: number;
    declared: number;
    /** Still expected. What the declare form pre-fills. */
    remaining: number;
}

export interface SiteOrderLine {
    workOrderId: string;
    code: string;
    productId: string;
    /** What a person calls it. Resolved by the server; the id stays for actions. */
    productName?: string | null;
    status: string;
    dueDate: string;
    /** What the brand asked for. */
    ordered: number;
    /** On the line right now: started and not yet declared off. Drains as production lands. */
    inProduction: number;
    /** What the ledger says exists. */
    produced: number;
    /**
     * Where the goods are NOW, rather than everything that ever happened to them.
     *
     * `qcApproved` is passed QC, in date and still here — what could go on a lorry today, and
     * exactly what the Dispatch queue offers. Cumulative "ever approved" belongs on the batches
     * register, which has an On hand column beside it to tell the two apart.
     *
     * Neither sums with `produced`: anything rejected or expired is in neither, and anything
     * shipped has left `qcApproved` for `dispatched`.
     */
    qcApproved: number;
    /**
     * Shelf stock virtually covering this line (FIFO by order date), already folded into
     * the figures above - carried separately so screens can mark it apart.
     */
    allocatedFromStock?: { total: number; approved: number; pending: number } | null;
    qcPending: number;
    /** What has actually left the site against this order, net of cancelled consignments. */
    dispatched: number;
    /**
     * This line's worth, tax in, as the order document prints it. Null on an unpriced line.
     * Optional because the field is additive — the server always sends it.
     */
    lineValue?: string | null;
    closeRequestedAt: string | null;
    /** Runs started against this line and not yet closed. */
    openRuns: SiteOpenRun[];
}

export interface SiteOrder {
    productionOrderId: string | null;
    code: string;
    status: string;
    expectedCompletionDate: string | null;
    /** True while nobody at the site has said they have seen it. */
    needsAcknowledgement: boolean;
    /** Whether the brand issued a PDF for this order that the site can open. */
    hasDocument: boolean;
    /** When the order was placed, and what it is worth — null on a headerless work order. */
    orderDate: string | null;
    total: string | null;
    currency: string | null;
    lines: SiteOrderLine[];
}

/** Open production orders at this site. The site comes from the login, never the request. */
export const getSiteOrders = async (): Promise<{ orders: SiteOrder[] }> =>
    ReparoApi({ method: 'GET', url: '/vendor-portal/orders' });

/**
 * The order document the brand issued, for the site that has to make it.
 *
 * A short-lived signed link, opened in a new tab rather than proxied through the app — the file
 * goes straight from storage to the browser. Only issued orders have one, which is why the
 * caller checks `hasDocument` before offering it.
 */
export const getOrderDocumentUrl = async (
    productionOrderId: string,
): Promise<{ url: string; expiresAt: string }> =>
    ReparoApi({
        method: 'GET',
        url: `/vendor-portal/orders/${productionOrderId}/document`,
    });

/**
 * Say the order has been seen and started — the brand's only signal that anyone read it.
 *
 * Reached by whichever key the job has: an order header when several SKUs were commissioned
 * together, the work order itself when a run was commissioned on its own. Both do the same
 * thing; a job without a header used to have no way to clear its own badge.
 */
export const acknowledgeOrder = async (order: {
    productionOrderId: string | null;
    workOrderId: string;
}): Promise<{ acknowledgedLines: number }> =>
    ReparoApi({
        method: 'POST',
        url: order.productionOrderId
            ? `/vendor-portal/orders/${order.productionOrderId}/acknowledge`
            : `/vendor-portal/work-orders/${order.workOrderId}/acknowledge`,
        body: JSON.stringify({}),
    });

/**
 * Say a run has started against an order.
 *
 * Names the batch up front, because the code is printed on the line as the run begins.
 * Declaring later under the same code lands on this batch and fills in the actual.
 */
export const startProduction = async (
    workOrderId: string,
    run: {
        code: string;
        quantity: number;
        manufacturingDate?: string;
        expiryDate?: string;
        notes?: string;
    },
): Promise<{ batchId: string; acknowledged: boolean }> =>
    ReparoApi({
        method: 'POST',
        url: `/vendor-portal/work-orders/${workOrderId}/start`,
        body: JSON.stringify(run),
    });

/**
 * Close a run that is finished, when less came off it than planned.
 *
 * Only for the short case: declaring the whole plan leaves nothing in production and needs no
 * second step. This exists so the missing units stop sitting in production forever, and it
 * changes neither the plan nor the actual — the variance stays visible.
 */
export const finishRun = async (batchId: string): Promise<unknown> =>
    ReparoApi({
        method: 'POST',
        url: `/vendor-portal/batches/${batchId}/complete`,
        body: JSON.stringify({}),
    });

/** Ask the brand to close a work order short. A request; the brand executes it. */
export const requestWorkOrderClose = async (
    workOrderId: string,
    body: { notes?: string; requestedBy?: string } = {},
): Promise<{ requestedAt: string }> =>
    ReparoApi({
        method: 'POST',
        url: `/vendor-portal/work-orders/${workOrderId}/request-close`,
        body: JSON.stringify(body),
    });

// ─── Dispatch ──────────────────────────────────────────────────

export interface DispatchResult {
    /** The shipment the brand will now see as in-transit. */
    purchaseOrderId: string;
    /** One per batch on the consignment, in the order they were sent. */
    eventIds: string[];
}

/**
 * One batch on a consignment — or one product out of the no-batch bucket, for a brand that
 * does not track finished-goods batches.
 *
 * The order is stated per line rather than read off the batch: a batch's own work-order link is
 * provenance, and a produce-to-stock batch has none at all while still being able to fill an
 * order. Left out when the goods are simply going back as stock.
 *
 * A line names a batch OR a product, never both. The server refuses a product line for a brand
 * that tracks batches.
 */
export interface DispatchLine {
    batchId?: string;
    /** The finished good, for stock with no batch. Omitted when a batch is named. */
    productId?: string;
    quantity: number;
    workOrderId?: string;
}

/**
 * What physically left, on one vehicle, on one day.
 *
 * THE GRAIN IS THE LORRY. Everything true of the whole load sits here; everything that differs
 * between the pallets on it sits on the lines. One batch is simply a consignment of one.
 */
export interface DispatchShipmentRequest {
    lines: DispatchLine[];
    /**
     * What the consignment is called — it becomes the shipment's own code, so it is what shows
     * in the purchase-order list, the ledger and the document. Omitted, a number is generated.
     */
    reference?: string;
    dispatchDate?: string;
    transportReference?: string;
    expectedDelivery?: string;
    warehouseId?: string;
    notes?: string;
    enteredByName?: string;
}

/**
 * Send finished goods from this site, as the site.
 *
 * NO SITE IN THE REQUEST. The server reads it off the login, so a manufacturer ships out of its
 * own yard and could not name another's if it tried. The brand's equivalent, `createShipment`,
 * has to be told which site it is acting for — that difference is the whole of the isolation.
 */
export const dispatchFromSite = async (
    dispatch: DispatchShipmentRequest,
): Promise<DispatchResult> =>
    ReparoApi({
        method: 'POST',
        url: '/vendor-portal/dispatch',
        body: JSON.stringify(dispatch),
    });

/**
 * Where this site may send finished goods: the brand's own warehouses.
 *
 * Its own endpoint because the brand's warehouse picker is not open to vendor logins — they are
 * refused every route that has not invited them, and a destination list is not confidential, it
 * is the address on the box.
 */
export const getSiteDestinations = async (): Promise<SiteDestination[]> =>
    ReparoApi({ method: 'GET', url: '/vendor-portal/destinations' });

/**
 * One door a site may send finished goods to.
 *
 * INTERNAL warehouses only, lane-backed ones first. The fields beyond `warehouseId` and `name`
 * are optional on this side so the brand's on-behalf call, typed before they existed, stays
 * assignable; the server sends them on both doors. `laneBacked` absent means "not known",
 * which the picker treats as the old flat list.
 */
export interface SiteDestination {
    warehouseId: string;
    name: string;
    /** The site's manufacturer has a lane ending here. */
    laneBacked?: boolean;
    /** The site's manufacturer, for "no lanes from X yet". Null for a site with no vendor. */
    vendorId?: string | null;
    vendorName?: string | null;
}

/** The name the next load will get if the site leaves the field blank. */
export const getNextShipmentReference = async (): Promise<{ reference: string }> =>
    ReparoApi({ method: 'GET', url: '/vendor-portal/next-shipment-reference' });

/**
 * One run, from made through checked to gone.
 *
 * The shape of the production book brands already keep in a spreadsheet. Every field was
 * already stored — this is a different grain on the same data, not new data.
 */
export interface BatchRegisterRow {
    /**
     * The batch's id — or, on a `noBatch` row, a synthetic key to select by. Never sent as a
     * batch: `toDispatchLines` turns such a row into a product line.
     */
    batchId: string;
    /** Empty on a `noBatch` row. */
    code: string;
    /**
     * Finished goods in the site's no-batch bucket, for a brand whose FG batch tracking is
     * off. One row per product and order, plus a "to stock" row with none. Shown as "No
     * batch" wherever a code would be.
     */
    noBatch?: boolean;
    itemId: string | null;
    itemName?: string | null;
    /** Which kind this is, so a reader can say "made" or "received" without guessing. */
    itemType: string | null;
    manufacturingDate: string | null;
    expiryDate: string | null;
    made: number;
    qcStatus: string | null;
    qcMarkedAt: string | null;
    /**
     * The split, when the verdict did not cover the whole batch. Null on both — the ordinary
     * case — means it did, and `made` is the quantity it applied to.
     */
    qcPassedQuantity: number | null;
    qcFailedQuantity: number | null;
    dispatched: number;
    lastDispatchedOn: string | null;
    /** Zero for a run that has entirely gone — kept, because that is a completed row. */
    remaining: number;
    workOrderId: string | null;
    /** FIFO's pick of which open order this free batch should fill - the dispatch default. */
    suggestedWorkOrderId?: string | null;
    suggestedProductionOrderId?: string | null;
    suggestedOrderCode?: string | null;
    /**
     * The order this batch was made against, named rather than only referenced, so a row can
     * say what it answers without resolving its id against a list of OPEN orders — which comes
     * back empty for an order since shipped or cancelled.
     */
    workOrderCode: string | null;
    productionOrderId: string | null;
    productionOrderCode: string | null;
}

/**
 * Every batch of one kind at this site, including runs that have entirely gone.
 *
 * The two figures mean different movements per kind and the screen relabels to match: a
 * finished batch is made and dispatched, a material batch is received and used.
 */
export const getBatchRegister = async (
    itemType: 'product' | 'material' = 'product',
): Promise<{ batches: BatchRegisterRow[] }> =>
    ReparoApi({ method: 'GET', url: `/vendor-portal/batches?itemType=${itemType}` });
